import QRCode from "qrcode";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MfaSetupConfirmButton,
  MfaSetupDescription,
  MfaSetupField,
  MfaSetupFieldLabel,
  MfaSetupFields,
  MfaSetupForm,
  MfaSetupHeader,
  MfaSetupHint,
  MfaSetupQrContainer,
  MfaSetupQrImage,
  MfaSetupQrLoading,
  MfaSetupSupport,
  MfaSetupTextField,
  MfaSetupTitle
} from "./MfaSetup.styles";
import { Spinner } from "../../components";
import { COGNITO_V3_CHALLENGE } from "../../consts";
import { useNavigate } from "../../services";
import { useAuthStore } from "../../stores";
import { getCustomLogicFeValue, getEnvironmentVariables } from "../../utils/general";

const OTP_MIN_LEN = 6;
const OTP_MAX_LEN = 8;
const ISSUER_NAME = "OpenLearning";

export function MfaSetup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customLogicFe } = getEnvironmentVariables();
  const emailSupport = getCustomLogicFeValue({
    envVariable: customLogicFe,
    strValueToCheck: "mail_support"
  });

  const isLoading = useAuthStore((state) => state.isLoading);
  const cognitoChallenge = useAuthStore((state) => state.cognitoChallenge);
  const totpSetupSecret = useAuthStore((state) => state.totpSetupSecret);
  const startMfaSetup = useAuthStore((state) => state.startMfaSetup);
  const verifyMfaSetup = useAuthStore((state) => state.verifyMfaSetup);
  const agreedToConditions = useAuthStore((state) => state.userInfo?.privacy_consent_flag === true);

  const [totpCode, setTotpCode] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");

  const isMfaSetupChallenge =
    cognitoChallenge?.challengeName === COGNITO_V3_CHALLENGE.MFA_SETUP;

  // Guard against direct access / stale refresh outside an active MFA_SETUP challenge.
  useEffect(() => {
    if (!isMfaSetupChallenge) {
      navigate("/access/login");
    }
  }, [isMfaSetupChallenge, navigate]);

  useEffect(() => {
    if (isMfaSetupChallenge && !totpSetupSecret) {
      startMfaSetup();
    }
  }, [isMfaSetupChallenge, startMfaSetup, totpSetupSecret]);

  const otpAuthUri = useMemo(() => {
    if (!totpSetupSecret || !cognitoChallenge?.username) {
      return "";
    }

    const accountName = cognitoChallenge.username;
    return (
      `otpauth://totp/${encodeURIComponent(ISSUER_NAME)}:${encodeURIComponent(accountName)}` +
      `?secret=${encodeURIComponent(totpSetupSecret)}` +
      `&issuer=${encodeURIComponent(ISSUER_NAME)}`
    );
  }, [cognitoChallenge?.username, totpSetupSecret]);

  useEffect(() => {
    let isMounted = true;

    if (!otpAuthUri) {
      setQrCodeDataUrl("");
      return;
    }

    QRCode.toDataURL(otpAuthUri, { margin: 1, width: 220 })
      .then((dataUrl: string) => {
        if (isMounted) {
          setQrCodeDataUrl(dataUrl);
        }
      })
      .catch(() => {
        if (isMounted) {
          setQrCodeDataUrl("");
        }
      });

    return () => {
      isMounted = false;
    };
  }, [otpAuthUri]);

  if (isLoading && !totpSetupSecret) {
    return <Spinner />;
  }

  const isCodeValid = totpCode.length >= OTP_MIN_LEN;

  return (
    <MfaSetupForm
      role="form"
    >
      <MfaSetupHeader>
        <MfaSetupTitle>
          { t("mfa_setup_title") }
        </MfaSetupTitle>
        <MfaSetupDescription>
          { t("mfa_setup_description") }
        </MfaSetupDescription>
      </MfaSetupHeader>

      <MfaSetupQrContainer>
        { qrCodeDataUrl ? (
          <MfaSetupQrImage
            src={ qrCodeDataUrl }
            alt={ t("mfa_setup_qr_alt") }
          />
        ) : (
          <MfaSetupQrLoading>
            <Spinner padding="0" size={ 48 } />
          </MfaSetupQrLoading>
        ) }
        <MfaSetupHint>
          { t("mfa_setup_qr_hint") }
        </MfaSetupHint>
      </MfaSetupQrContainer>

      <MfaSetupFields>
        <MfaSetupField>
          <MfaSetupFieldLabel>
            { t("mfa_setup_manual_secret") }
          </MfaSetupFieldLabel>
          <MfaSetupTextField
            disabled
            fullWidth
            hiddenLabel
            inputProps={ {
              "aria-label": t("mfa_setup_manual_secret")
            } }
            placeholder={ t("mfa_setup_manual_secret") }
            value={ totpSetupSecret }
          />
        </MfaSetupField>

        <MfaSetupField>
          <MfaSetupFieldLabel>
            { t("mfa_setup_code_label") }
          </MfaSetupFieldLabel>
          <MfaSetupTextField
            autoComplete="one-time-code"
            fullWidth
            hiddenLabel
            id="mfa-setup-code"
            inputProps={ {
              "aria-label": t("mfa_setup_code_label"),
              inputMode: "numeric",
              maxLength: OTP_MAX_LEN
            } }
            onChange={ (event: ChangeEvent<HTMLInputElement>) => {
              const sanitized = event.target.value.replace(/\s+/g, "").slice(0, OTP_MAX_LEN);
              setTotpCode(sanitized);
            } }
            placeholder={ t("mfa_setup_code_label") }
            value={ totpCode }
          />
        </MfaSetupField>
      </MfaSetupFields>

      <MfaSetupConfirmButton
        disabled={ !isCodeValid || isLoading }
        onClick={ () => {
          verifyMfaSetup(
            totpCode,
            {
              state: agreedToConditions ? {} : { first: true, isAuthenticated: true },
              target: agreedToConditions ? "/" : "/agreements"
            },
            true
          );
        } }
        type="button"
        variant="contained"
      >
        { t("confirm") }
      </MfaSetupConfirmButton>

      { emailSupport && (
        <MfaSetupSupport href={ `mailto:${emailSupport}` }>
          { t("mfa_setup_support") }
        </MfaSetupSupport>
      ) }
    </MfaSetupForm>
  );
}
